// UnidadesTable.js
import React, { useEffect, useState, useMemo } from "react";
import { useTable, useFilters, usePagination, useSortBy } from "react-table";

const UnidadesTable = ({
  columns,
  data,
  loading = false,
  totalPages = 1,
  currentPage = 1,
  totalRecords = 0,
  fetchUrl,
  handleSortingChange,
  sortField,
  order,
  page,
  nextUrl,
  previousUrl,
}) => {
  const hasData = Array.isArray(data) && data.length > 0;

  const tableInstance = useTable(
    {
      columns,
      data: hasData ? data : [],
      manualPagination: true,
      pageCount: totalPages || 1,
    },
    useFilters,
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page: internalPage,
    setPageSize,
  } = tableInstance;

  const effectivePage = useMemo(() => {
    if (page !== undefined && page !== null) {
      if (Array.isArray(page) && page.length > 0) {
        return page;
      }
      if (Array.isArray(page) && page.length === 0) {
        return hasData ? internalPage : [];
      }
    }
    return hasData ? internalPage : [];
  }, [page, internalPage, hasData]);

  const pageLength = effectivePage?.length ?? 0;

  useEffect(() => {
    if (typeof setPageSize === "function") {
      const dataLength = hasData ? data.length : 0;
      const size = dataLength || pageLength || 10;
      setPageSize(size);
    }
  }, [data, pageLength, setPageSize, hasData]);

  const [pageNumber, setPageNumber] = useState("");

  let typingTimer;

  const handleChange = (e) => {
    const value = e.target.value;
    setPageNumber(value);

    clearTimeout(typingTimer);

    typingTimer = setTimeout(() => {
      if (/^\d+$/.test(value)) {
        const pageNum = parseInt(value, 10);
        if (fetchUrl && pageNum > 0 && pageNum <= totalPages) {
          fetchUrl(pageNum);
        }
      }
    }, 700);
  };

  const showPagination = totalPages > 1;

  return (
    <>
      <div className="filter">
        <div className="table-responsive dataTables_wrapper">
          <table {...getTableProps()} className="table display">
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key: headerGroupKey, ...headerGroupProps } =
                  headerGroup.getHeaderGroupProps();
                return (
                  <tr key={headerGroupKey} {...headerGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const { key: columnKey, ...columnProps } =
                        column.getHeaderProps();
                      return (
                        <th
                          key={columnKey}
                          {...columnProps}
                          onClick={() =>
                            column.disableSortable
                              ? null
                              : handleSortingChange?.(column)
                          }
                          style={{
                            cursor: column.disableSortable ? "auto" : "pointer",
                          }}
                        >
                          {column.render("Header")}
                          {column.canFilter ? column.render("Filter") : null}
                          {column.canSort && (
                            <span className="ms-1">
                              {column.id === sortField ? (
                                order === "asc" ? (
                                  <i className="fa fa-arrow-down" />
                                ) : (
                                  <i className="fa fa-arrow-up" />
                                )
                              ) : (
                                <i className="fa fa-sort text-muted" />
                              )}
                            </span>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()} className="">
              {effectivePage?.map?.((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td key={cellKey} {...cellProps}>
                          {" "}
                          {cell.render("Cell")}{" "}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
            <div className="dataTables_info">
              Pagina {currentPage} de {totalPages} total registros{" "}
              {totalRecords}
            </div>

            {showPagination && (
              <>
                <span className="table-index dataTables_info">
                  Ir a pagina
                  <input
                    type="text"
                    className="dataTables_length form-control form-control-sm d-inline-block mx-2 border-0 text-center w-25"
                    defaultValue={currentPage}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      const keyCode = e.keyCode || e.which;

                      if (keyCode < 48 || keyCode > 57) {
                        e.preventDefault();
                      }
                    }}
                  />
                </span>

                <div className="dataTables_paginate paging_simple_numbers">
                  <button
                    className="paginate_button previous disabled transparent-button"
                    onClick={() => fetchUrl?.(1)}
                    disabled={currentPage === 1}
                  >
                    <i
                      className="fa fa-angle-double-left"
                      aria-hidden="true"
                    ></i>
                  </button>

                  <button
                    className="paginate_button previous transparent-button"
                    onClick={() => fetchUrl?.(currentPage - 1)}
                    disabled={!previousUrl}
                  >
                    <i className="fa fa-angle-left" aria-hidden="true"></i>
                  </button>

                  <button
                    className="paginate_button next transparent-button"
                    onClick={() => fetchUrl?.(currentPage + 1)}
                    disabled={!nextUrl}
                  >
                    <i className="fa fa-angle-right" aria-hidden="true"></i>
                  </button>

                  <button
                    className="paginate_button next transparent-button"
                    onClick={() => fetchUrl?.(totalPages)}
                    disabled={totalPages === currentPage}
                  >
                    <i
                      className="fa fa-angle-double-right"
                      aria-hidden="true"
                    ></i>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UnidadesTable;
